import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/hooks/useLanguage";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowUpDown, Plus, User2, Users, Globe } from "lucide-react";
import CharacterPreview from "@/components/character/CharacterPreview";
import { useIsMobile } from "@/hooks/use-mobile";

export type Character = {
  id: string;
  name: string;
  description: string | null;
  avatar_url: string | null;
  role: string | null;
  personality_traits: string[] | null;
  strengths: string[] | null;
  weaknesses: string[] | null;
  skills: string[] | null;
  translation_overrides: Record<string, any> | null;
  is_public: boolean;
  user_id: string;
  author?: string;
  average_rating?: number | null;
};

const CharactersPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("my-characters");
  const [filterRole, setFilterRole] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };
    getCurrentUser();
  }, []);

  const { data: characters, isLoading, refetch } = useQuery({
    queryKey: ["characters", activeTab, sortBy, filterRole, searchQuery, currentUserId],
    queryFn: async () => {
      let query = supabase
        .from("characters")
        .select(`
          *,
          profiles:user_id (
            display_name
          )
        `);

      if (activeTab === "my-characters") {
        query = query.eq("user_id", currentUserId);
      } else if (activeTab === "public-characters") {
        query = query.eq("is_public", true);
      }

      if (filterRole && filterRole !== "all") {
        query = query.eq("role", filterRole);
      }

      if (searchQuery) {
        query = query.ilike("name", `%${searchQuery}%`);
      }

      if (sortBy === "rating") {
        query = query.order("average_rating", { ascending: false, nullsFirst: false });
      } else if (sortBy === "name") {
        query = query.order("name");
      } else {
        query = query.order("created_at", { ascending: false });
      }

      const { data, error } = await query;

      if (error) {
        toast({
          variant: "destructive",
          title: t("errors.error"),
          description: t("errors.loadCharactersError"),
        });
        return [];
      }

      return (data || []).map((char): Character => ({
        id: char.id,
        name: char.name || "",
        description: char.description,
        avatar_url: char.avatar_url,
        role: char.role,
        personality_traits: char.personality_traits || [],
        strengths: char.strengths || [],
        weaknesses: char.weaknesses || [],
        skills: char.skills || [],
        translation_overrides: char.translation_overrides as Record<string, any> || null,
        is_public: char.is_public || false,
        user_id: char.user_id,
        author: char.profiles?.display_name || 'Anonymous',
        average_rating: char.average_rating
      }));
    },
    enabled: !!currentUserId,
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Button
          onClick={() => navigate("/characters/new")}
          className="bg-[#7E69AB] hover:bg-[#6E59A5]"
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('dashboard.createCharacter.new')}
        </Button>
      </div>

      <Tabs defaultValue="my-characters" className="w-full" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 bg-white/5">
          <TabsTrigger value="my-characters" className="data-[state=active]:bg-white/10">
            <User2 className="w-4 h-4 mr-2" />
            {t('dashboard.characterTabs.myCharacters')}
          </TabsTrigger>
          <TabsTrigger value="public-characters" className="data-[state=active]:bg-white/10">
            <Globe className="w-4 h-4 mr-2" />
            {t('dashboard.characterTabs.publicCharacters')}
          </TabsTrigger>
        </TabsList>

        <div className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Input
              placeholder={t('dashboard.search.placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-white/10 text-white placeholder:text-white/60"
              maxLength={2000}
            />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-white/10 text-white">
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="h-4 w-4" />
                  <span>{t('dashboard.filters.sortBy')}</span>
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">{t('dashboard.filters.name')}</SelectItem>
                <SelectItem value="created_at">{t('dashboard.filters.dateCreated')}</SelectItem>
                <SelectItem value="rating">{t('dashboard.filters.rating')}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="bg-white/10 text-white w-full">
                <span>{t('dashboard.filters.role')}</span>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('dashboard.filters.allRoles')}</SelectItem>
                <SelectItem value="protagonist">{t('dashboard.filters.roles.protagonist')}</SelectItem>
                <SelectItem value="antagonist">{t('dashboard.filters.roles.antagonist')}</SelectItem>
                <SelectItem value="supporting">{t('dashboard.filters.roles.supporting')}</SelectItem>
                <SelectItem value="mentor">{t('dashboard.filters.roles.mentor')}</SelectItem>
                <SelectItem value="comic_relief">{t('dashboard.filters.roles.comic_relief')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <TabsContent value="my-characters" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {!isLoading && characters?.map((character) => (
              <CharacterPreview
                key={character.id}
                character={character}
                enableNavigation={true}
                onDelete={() => refetch()}
              />
            ))}
          </div>
        </TabsContent>

        <TabsContent value="public-characters" className="mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {!isLoading && characters?.map((character) => (
              <CharacterPreview
                key={character.id}
                character={character}
                enableNavigation={true}
                onDelete={() => refetch()}
              />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CharactersPage;
